import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Download, Link2, Paperclip, Send } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BRIEF_SECTIONS, WEB_ELEMENT_STATUS, webStageLabel } from "@/lib/constants";
import { fmtD } from "@/components/graphisme/gUtils";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STEPS = [
  { label: "Cahier des charges", stages: ["creation", "cahier_des_charges"] },
  { label: "Design", stages: ["collecte", "design"] },
  { label: "Développement", stages: ["developpement", "seo"] },
  { label: "Validation", stages: ["tests", "validation_finale", "prete_mise_en_ligne"] },
  { label: "Mise en ligne", stages: ["en_ligne", "recette", "cloture"] },
];

const TABS = [
  ["suivi", "Suivi de mon projet"], ["elements", "Éléments à fournir"],
  ["validations", "Validations"], ["documents", "Mes documents"], ["messages", "Messages"],
];

export default function WebEspaceProjet() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [tab, setTab] = useState("suivi");

  const load = () => api.get(`/web/espace/projects/${pid}`).then((r) => setP(r.data)).catch(() => setP(false));
  useEffect(() => {
    api.get("/graphisme/auth/me").then(load).catch(() => navigate("/connexion"));
  }, [pid]);

  if (p === null) return <div className="min-h-screen bg-neutral-100 flex items-center justify-center"><div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>;
  if (p === false) return <p className="py-20 text-center text-neutral-400">Projet introuvable.</p>;

  const stepIdx = Math.max(0, STEPS.findIndex((s) => s.stages.includes(p.stage)));
  const missing = (p.elements || []).filter((e) => ["a_fournir", "correction_demandee"].includes(e.status)).length;

  return (
    <div className="min-h-screen bg-neutral-100" data-testid="wclient-page">
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <button onClick={() => navigate("/graphisme/espace")} className="text-xs text-neutral-400 hover:text-neutral-900 flex items-center gap-1 mb-2" data-testid="wclient-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Mon espace
          </button>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h1 className="font-heading font-bold text-lg" data-testid="wclient-name">{p.name}</h1>
              <p className="text-xs text-neutral-500">Site web · {webStageLabel(p.stage)}</p>
            </div>
            <div className="flex items-center gap-2 min-w-40">
              <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
                <div className="h-full bg-neutral-900 rounded-full transition-all" style={{ width: `${p.progress}%` }} />
              </div>
              <span className="text-sm font-semibold" data-testid="wclient-progress">{p.progress}%</span>
            </div>
          </div>
        </div>
        <nav className="max-w-3xl mx-auto px-4 flex gap-1 overflow-x-auto pb-2" data-testid="wclient-nav">
          {TABS.map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`wclient-tab-${id}`}
              className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${tab === id ? "bg-neutral-900 text-white" : "text-neutral-500 hover:text-neutral-900"}`}>
              {label}{id === "elements" && missing > 0 ? ` (${missing})` : ""}
            </button>
          ))}
        </nav>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {tab === "suivi" && <Suivi p={p} stepIdx={stepIdx} onBriefSaved={load} />}
        {tab === "elements" && <Elements p={p} onDone={load} />}
        {tab === "validations" && <Validations p={p} onDone={load} />}
        {tab === "documents" && <Documents p={p} />}
        {tab === "messages" && <Messages p={p} onDone={load} />}
      </main>
    </div>
  );
}

function Suivi({ p, stepIdx, onBriefSaved }) {
  return (
    <div className="space-y-5">
      <div className="bg-white border border-neutral-200 rounded-2xl p-5" data-testid="wclient-frise">
        <div className="flex items-center">
          {STEPS.map((s, i) => (
            <div key={s.label} className="flex-1 flex flex-col items-center relative">
              {i > 0 && <div className={`absolute top-3 left-0 right-1/2 h-0.5 ${i <= stepIdx ? "bg-neutral-900" : "bg-neutral-200"}`} style={{ right: "50%" }} />}
              {i < STEPS.length - 1 && <div className={`absolute top-3 left-1/2 right-0 h-0.5 ${i < stepIdx ? "bg-neutral-900" : "bg-neutral-200"}`} />}
              <div className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${i < stepIdx ? "bg-neutral-900 text-white" : i === stepIdx ? "bg-neutral-900 text-white ring-4 ring-neutral-200" : "bg-neutral-200 text-neutral-400"}`}>
                {i < stepIdx ? "✓" : i + 1}
              </div>
              <p className={`text-[10px] mt-1.5 text-center ${i === stepIdx ? "font-bold" : "text-neutral-400"}`}>{s.label}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-neutral-100 flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <span className="text-neutral-500">Étape actuelle : <strong>{webStageLabel(p.stage)}</strong></span>
          {p.delivery_date && <span className="text-neutral-500">Livraison prévisionnelle : <strong>{fmtD(p.delivery_date)}</strong></span>}
        </div>
        {p.production_url && (
          <a href={p.production_url} target="_blank" rel="noreferrer" data-testid="wclient-site-url"
            className="mt-3 inline-flex items-center gap-1.5 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 font-medium">
            <CheckCircle2 className="w-4 h-4" /> Votre site est en ligne — {p.production_url}
          </a>
        )}
      </div>

      {(p.activity || []).length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-2xl p-5">
          <h3 className="font-heading font-bold text-sm mb-2">Dernières actualités</h3>
          {[...p.activity].reverse().slice(0, 8).map((a, i) => (
            <p key={i} className="text-xs text-neutral-500 py-0.5">{fmtD(a.at)} — {a.text}</p>
          ))}
        </div>
      )}

      <BriefForm p={p} onSaved={onBriefSaved} />
    </div>
  );
}

function BriefForm({ p, onSaved }) {
  const brief = p.brief || {};
  const [answers, setAnswers] = useState(brief.answers || {});
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(!brief.completed && !brief.validated);
  useEffect(() => { setAnswers(p.brief?.answers || {}); }, [p.id]);

  const save = async (completed) => {
    setBusy(true);
    try {
      await api.put(`/web/espace/projects/${p.id}/brief`, { answers, completed });
      toast.success(completed ? "Cahier des charges envoyé à votre webmaster" : "Réponses enregistrées — vous pourrez les compléter plus tard");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-5" data-testid="wclient-brief">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between" data-testid="wclient-brief-toggle">
        <h3 className="font-heading font-bold text-sm">Cahier des charges</h3>
        <span className="text-xs text-neutral-400">
          {brief.validated ? "Validé ✓" : brief.completed ? "Envoyé — en cours de relecture" : open ? "Réduire ▴" : "À compléter ▾"}
        </span>
      </button>
      {open && (
        <div className="mt-4 space-y-4">
          {brief.validated && <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">Ce cahier des charges a été validé. Contactez votre webmaster pour toute modification.</p>}
          {BRIEF_SECTIONS.map((sec) => (
            <div key={sec.id}>
              <h4 className="text-xs font-semibold text-neutral-500 mb-2">{sec.title}</h4>
              <div className="space-y-2.5">
                {sec.questions.map(([key, label]) => (
                  <div key={key}>
                    <p className="text-xs text-neutral-400 mb-1">{label}</p>
                    <Textarea rows={2} value={answers[key] || ""} disabled={brief.validated}
                      onChange={(e) => setAnswers({ ...answers, [key]: e.target.value })}
                      className="text-sm" data-testid={`wclient-brief-${key}`} />
                  </div>
                ))}
              </div>
            </div>
          ))}
          {(brief.custom_questions || []).map((q) => (
            <div key={q.id}>
              <p className="text-xs text-neutral-400 mb-1">{q.label}</p>
              <Textarea rows={2} value={answers[`custom_${q.id}`] || ""} disabled={brief.validated}
                onChange={(e) => setAnswers({ ...answers, [`custom_${q.id}`]: e.target.value })}
                className="text-sm" data-testid={`wclient-brief-custom-${q.id}`} />
            </div>
          ))}
          {!brief.validated && (
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => save(false)} disabled={busy} data-testid="wclient-brief-save">
                Enregistrer mes réponses
              </Button>
              {!brief.completed && (
                <Button size="sm" className="bg-neutral-900 hover:bg-neutral-800 text-white" onClick={() => save(true)} disabled={busy} data-testid="wclient-brief-complete">
                  J'ai terminé — envoyer au webmaster
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Elements({ p, onDone }) {
  const els = p.elements || [];
  const recus = els.filter((e) => ["recu", "valide"].includes(e.status)).length;
  return (
    <div className="space-y-3" data-testid="wclient-elements">
      <div className="flex items-center justify-between">
        <h2 className="font-heading font-bold text-lg">Éléments à fournir</h2>
        <span className="text-xs text-neutral-500" data-testid="wclient-elements-count">{recus} élément(s) reçu(s) sur {els.length}</span>
      </div>
      {els.length === 0 && <p className="text-sm text-neutral-400 bg-white border border-dashed border-neutral-300 rounded-xl p-6 text-center">Aucun élément demandé pour le moment.</p>}
      {els.map((e) => <ElementCard key={e.id} p={p} e={e} onDone={onDone} />)}
    </div>
  );
}

function ElementCard({ p, e, onDone }) {
  const st = WEB_ELEMENT_STATUS[e.status] || WEB_ELEMENT_STATUS.a_fournir;
  const [files, setFiles] = useState([]);
  const [text, setText] = useState("");
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const canSubmit = ["a_fournir", "correction_demandee"].includes(e.status);

  const submit = async () => {
    if (files.length === 0 && !text.trim() && !link.trim()) {
      toast.error("Ajoutez un fichier, un texte ou un lien"); return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("text", text); fd.append("link", link);
      await api.post(`/web/espace/projects/${p.id}/elements/${e.id}/submit`, fd);
      toast.success(`« ${e.name} » transmis à votre webmaster`);
      setFiles([]); setText(""); setLink("");
      onDone?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur lors de l'envoi"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4" data-testid={`wclient-element-${e.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold">{e.name}</p>
          {e.description && <p className="text-xs text-neutral-500 mt-0.5">{e.description}</p>}
          {e.instructions && <p className="text-xs text-neutral-400 mt-0.5 italic">{e.instructions}</p>}
          <p className="text-[11px] text-neutral-400 mt-1">
            {e.quantity > 1 ? `${e.quantity} souhaités · ` : ""}{e.due_date ? `À transmettre avant le ${fmtD(e.due_date)}` : "Sans échéance"}
          </p>
        </div>
        <span className={`text-[11px] border rounded-full px-2 py-0.5 shrink-0 ${st.cls}`}>{st.label}</span>
      </div>
      {e.status === "correction_demandee" && e.review_comment && (
        <p className="text-xs text-orange-800 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 mt-2" data-testid={`wclient-correction-${e.id}`}>
          Correction demandée : « {e.review_comment} »
        </p>
      )}
      {(e.submissions || []).length > 0 && (
        <p className="text-[11px] text-neutral-400 mt-2">
          {e.submissions.length} transmission(s) — dernière le {fmtD(e.submissions[e.submissions.length - 1].at)}
        </p>
      )}
      {canSubmit && (
        <div className="mt-3 pt-3 border-t border-neutral-100 space-y-2">
          <div className="flex gap-2 flex-wrap">
            <input ref={fileRef} type="file" multiple className="hidden"
              onChange={(ev) => setFiles(Array.from(ev.target.files || []))} data-testid={`wclient-file-${e.id}`} />
            <Button type="button" variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={() => fileRef.current?.click()} data-testid={`wclient-file-btn-${e.id}`}>
              <Paperclip className="w-3.5 h-3.5" /> {files.length > 0 ? `${files.length} fichier(s) choisi(s)` : "Ajouter un fichier"}
            </Button>
            {e.kind === "lien" && (
              <div className="flex items-center gap-1.5 flex-1 min-w-40">
                <Link2 className="w-3.5 h-3.5 text-neutral-400" />
                <Input className="!h-8 text-xs" placeholder="https://…" value={link} onChange={(ev) => setLink(ev.target.value)} data-testid={`wclient-link-${e.id}`} />
              </div>
            )}
          </div>
          {["texte", "document"].includes(e.kind) && (
            <Textarea rows={2} className="text-sm" placeholder="Rédigez votre texte ici…" value={text} onChange={(ev) => setText(ev.target.value)} data-testid={`wclient-text-${e.id}`} />
          )}
          {files.length > 0 && <p className="text-[11px] text-neutral-400">{files.map((f) => f.name).join(", ")}</p>}
          <Button size="sm" className="bg-neutral-900 hover:bg-neutral-800 text-white h-8 text-xs" onClick={submit} disabled={busy} data-testid={`wclient-submit-${e.id}`}>
            {busy ? "Envoi…" : "Transmettre"}
          </Button>
        </div>
      )}
    </div>
  );
}

function Validations({ p, onDone }) {
  const els = p.elements || [];
  const enCours = els.filter((e) => e.status === "recu");
  const valides = els.filter((e) => e.status === "valide");
  const validations = p.validations || [];
  return (
    <div className="space-y-3" data-testid="wclient-validations">
      <h2 className="font-heading font-bold text-lg">Validations</h2>
      {validations.filter((v) => v.status === "envoyee").map((v) => (
        <ValidationCard key={v.id} p={p} v={v} onDone={onDone} />
      ))}
      {validations.filter((v) => v.status !== "envoyee").length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-xl p-4 space-y-2">
          <p className="text-xs font-semibold text-neutral-400">Historique des validations</p>
          {validations.filter((v) => v.status !== "envoyee").map((v) => (
            <div key={v.id} className="text-sm" data-testid={`wclient-validation-history-${v.id}`}>
              <p className="font-medium">{v.label}</p>
              <p className={`text-xs ${v.status === "validee" ? "text-green-700" : "text-orange-700"}`}>
                {v.status === "validee" ? "✓ Validée" : "Modifications demandées"} le {fmtD(v.decided_at)}
                {v.comment ? ` — « ${v.comment} »` : ""}
              </p>
            </div>
          ))}
        </div>
      )}
      <div className="bg-white border border-neutral-200 rounded-xl p-4">
        <p className="text-sm font-medium">Cahier des charges</p>
        <p className="text-xs text-neutral-500 mt-0.5" data-testid="wclient-validation-brief">
          {p.brief?.validated ? "✓ Validé" : p.brief?.completed ? "Envoyé — en cours de relecture par le webmaster" : "Pas encore complété (onglet Suivi de mon projet)"}
        </p>
      </div>
      {enCours.map((e) => (
        <div key={e.id} className="bg-white border border-neutral-200 rounded-xl p-4" data-testid={`wclient-validation-encours-${e.id}`}>
          <p className="text-sm font-medium">{e.name}</p>
          <p className="text-xs text-blue-700 mt-0.5">Transmis — vérification en cours par le webmaster</p>
        </div>
      ))}
      {valides.map((e) => (
        <div key={e.id} className="bg-white border border-neutral-200 rounded-xl p-4" data-testid={`wclient-validation-ok-${e.id}`}>
          <p className="text-sm font-medium">{e.name}</p>
          <p className="text-xs text-green-700 mt-0.5">✓ Validé</p>
        </div>
      ))}
      {validations.length === 0 && enCours.length === 0 && valides.length === 0 && (
        <p className="text-sm text-neutral-400 bg-white border border-dashed border-neutral-300 rounded-xl p-6 text-center">
          Aucun livrable en validation pour le moment.
        </p>
      )}
    </div>
  );
}

function ValidationCard({ p, v, onDone }) {
  const [mode, setMode] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const dv = v.design_version || {};
  const answer = async (action) => {
    if (action === "modifications" && !comment.trim()) {
      toast.error("Précisez les modifications souhaitées"); return;
    }
    setBusy(true);
    try {
      await api.post(`/web/espace/projects/${p.id}/validations/${v.id}/answer`, { action, comment });
      toast.success(action === "valide" ? "Validation enregistrée — merci !" : "Demande de modifications envoyée");
      setMode(null); setComment("");
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-blue-200 rounded-xl p-4 space-y-3" data-testid={`wclient-validation-${v.id}`}>
      <div>
        <p className="text-sm font-semibold">{v.label}</p>
        <p className="text-xs text-neutral-400">Reçue le {fmtD(v.requested_at)}{v.version_label ? ` · version ${v.version_label}` : ""}</p>
      </div>
      {(dv.links || []).map((l, i) => (
        <a key={i} href={l} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-blue-600 hover:underline" data-testid={`wclient-validation-link-${v.id}-${i}`}>
          <Link2 className="w-3.5 h-3.5" /> {l}
        </a>
      ))}
      {(dv.files || []).map((f) => (
        <a key={f.id} href={`${API}/web/espace/files/${f.id}/view`} target="_blank" rel="noreferrer"
          className="flex items-center gap-1.5 text-xs text-blue-600 hover:underline" data-testid={`wclient-validation-file-${f.id}`}>
          <Paperclip className="w-3.5 h-3.5" /> {f.name}
        </a>
      ))}
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white h-8 text-xs" onClick={() => answer("valide")} disabled={busy} data-testid={`wclient-validate-${v.id}`}>
          Valider
        </Button>
        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setMode(mode === "modif" ? null : "modif")} data-testid={`wclient-ask-modif-${v.id}`}>
          Demander des modifications
        </Button>
      </div>
      {mode === "modif" && (
        <div className="space-y-2">
          <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)}
            placeholder="Décrivez précisément les modifications souhaitées (obligatoire)…" data-testid={`wclient-modif-comment-${v.id}`} />
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => answer("modifications")} disabled={busy || !comment.trim()} data-testid={`wclient-modif-send-${v.id}`}>
            Envoyer la demande
          </Button>
        </div>
      )}
    </div>
  );
}

function Documents({ p }) {
  const files = (p.elements || []).flatMap((e) =>
    (e.submissions || []).flatMap((s) =>
      (s.files || []).map((f) => ({ ...f, element: e.name, at: s.at }))));
  return (
    <div className="space-y-3" data-testid="wclient-documents">
      <h2 className="font-heading font-bold text-lg">Mes documents</h2>
      {files.length === 0 ? (
        <p className="text-sm text-neutral-400 bg-white border border-dashed border-neutral-300 rounded-xl p-6 text-center">
          Aucun document transmis pour ce projet. Vos devis et factures sont dans « Mes documents » sur la page d'accueil de votre espace.
        </p>
      ) : (
        files.map((f) => (
          <div key={f.id} className="bg-white border border-neutral-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3" data-testid={`wclient-doc-${f.id}`}>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{f.name}</p>
              <p className="text-xs text-neutral-400">{f.element} · transmis le {fmtD(f.at)}</p>
            </div>
            <a href={`${API}/web/espace/files/${f.id}`} className="text-neutral-400 hover:text-neutral-900 shrink-0" data-testid={`wclient-doc-dl-${f.id}`}>
              <Download className="w-4 h-4" />
            </a>
          </div>
        ))
      )}
    </div>
  );
}

function Messages({ p, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/web/espace/projects/${p.id}/messages`, { text: text.trim() });
      setText("");
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-5 space-y-4" data-testid="wclient-messages">
      <h2 className="font-heading font-bold text-lg">Messages</h2>
      <div className="space-y-2 max-h-96 overflow-y-auto">
        {(p.messages || []).length === 0 && <p className="text-sm text-neutral-400">Aucun échange pour le moment — posez votre question ici.</p>}
        {(p.messages || []).map((m) => (
          <div key={m.id} className={`rounded-lg px-3 py-2 text-sm ${m.by_client ? "bg-neutral-900 text-white ml-8" : "bg-neutral-50 border border-neutral-100 mr-8"}`} data-testid={`wclient-message-${m.id}`}>
            <p className={`text-[11px] mb-0.5 ${m.by_client ? "text-neutral-400" : "text-neutral-400"}`}>{m.by} · {fmtD(m.at)}</p>
            {m.text}
          </div>
        ))}
      </div>
      <div className="flex items-end gap-2">
        <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Votre message…" data-testid="wclient-message-input" />
        <Button size="sm" className="bg-neutral-900 hover:bg-neutral-800 text-white" onClick={send} disabled={busy} data-testid="wclient-message-send">
          <Send className="w-4 h-4" />
        </Button>
      </div>
      {p.maintenance?.active && <ReportProblem p={p} onDone={onDone} />}
    </div>
  );
}

function ReportProblem({ p, onDone }) {
  const [f, setF] = useState({ subject: "", description: "", url: "", urgency: "normale" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.subject.trim()) { toast.error("Indiquez l'objet du problème"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("file", file);
      await api.post(`/web/espace/projects/${p.id}/reports`, fd);
      toast.success("Problème signalé à votre webmaster");
      setF({ subject: "", description: "", url: "", urgency: "normale" }); setFile(null);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  return (
    <div className="pt-4 border-t border-neutral-100 space-y-2" data-testid="wclient-report">
      <p className="text-xs font-semibold text-neutral-500">Un problème sur votre site ? (maintenance)</p>
      <Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="Objet (ex : formulaire de contact bloqué)" data-testid="wclient-report-subject" />
      <Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Description du problème…" data-testid="wclient-report-description" />
      <div className="flex gap-2 flex-wrap">
        <Input className="flex-1 min-w-40" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="Page concernée (URL, optionnel)" data-testid="wclient-report-url" />
        <select value={f.urgency} onChange={(e) => setF({ ...f, urgency: e.target.value })}
          className="border border-neutral-200 rounded-md px-2 py-2 text-sm bg-white" data-testid="wclient-report-urgency">
          <option value="basse">Urgence basse</option><option value="normale">Urgence normale</option>
          <option value="haute">Urgence haute</option><option value="urgente">Urgent</option>
        </select>
        <Input type="file" className="text-xs w-44" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="wclient-report-file" />
      </div>
      <Button size="sm" className="bg-neutral-900 hover:bg-neutral-800 text-white h-8 text-xs" onClick={submit} disabled={busy} data-testid="wclient-report-send">
        Signaler le problème
      </Button>
    </div>
  );
}
